import './lib/sentry'
import './lib/configure-api'
import './lib/chunk-reload'
import { useEffect } from 'react'
import { QueryClient } from '@tanstack/react-query'
import { createRouter as createTanStackRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { routerWithQueryClient } from '@tanstack/react-router-with-query'
import { Sentry } from './lib/sentry'
import { routeTree } from './routeTree.gen'
import { RoutePending } from './modules/shared/components/route-pending'

/**
 * Router-level catch boundary. TanStack Router swallows route render errors in
 * its own boundaries (React error boundaries never see them), so we report them
 * to Sentry here. The SDK's global handlers already cover errors thrown outside
 * React (boot/chunk-load failures).
 */
function DefaultErrorComponent({ error }: ErrorComponentProps) {
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <h1 className="text-lg font-semibold text-[#1A1410]">Something went wrong</h1>
      <p className="max-w-sm text-sm text-[#6b6357]">
        We hit an unexpected error and our team has been notified. Try reloading the page.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="btn btn-primary"
      >
        Reload
      </button>
    </div>
  )
}

export function getRouter() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
      },
    },
  })

  const router = routerWithQueryClient(
    createTanStackRouter({
      routeTree,
      context: { queryClient },
      defaultPreload: 'intent',
      defaultErrorComponent: DefaultErrorComponent,
      defaultPendingComponent: RoutePending,
      defaultPendingMs: 150,
      defaultPendingMinMs: 400,
      scrollRestoration: true,
    }),
    queryClient,
  )

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
}
